import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { apiService } from '../../services/api'
import { 
  ArrowLeft, 
  ShoppingCart,
  User,
  Building2,
  DollarSign,
  FileText,
  Truck,
  MessageSquare,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Edit,
  Star
} from 'lucide-react'
import { toast } from 'react-toastify'

interface PurchaseOrderDetail {
  id: string
  poNumber: string
  status: string
  totalAmount: number
  currency: string
  notes?: string
  createdAt: string
  updatedAt: string
  rfq: {
    id: string
    title: string
    description?: string
    buyer: {
      id: string
      email: string
      firstName?: string
      lastName?: string
      companyName?: string
    }
  }
  rfqResponse: {
    id: string
    vendor: {
      id: string
      email: string
      firstName?: string
      lastName?: string
      companyName?: string
    }
  }
  buyer: {
    id: string
    email: string
    firstName?: string
    lastName?: string
    companyName?: string
  }
  vendor: {
    id: string
    email: string
    firstName?: string
    lastName?: string
    companyName?: string
  }
  fulfillments: Array<{
    id: string
    status: string
    trackingNumber?: string
    carrierName?: string
    estimatedDelivery?: string
    actualDelivery?: string
    createdAt: string
  }>
  invoices: Array<{
    id: string
    invoiceNumber: string
    status: string
    amount: number
    dueDate: string
    createdAt: string
  }>
  feedback: Array<{
    id: string
    rating: number
    review?: string
    createdAt: string
    buyer: {
      id: string
      email: string
      firstName?: string
      lastName?: string
      companyName?: string
    }
  }>
}

const PurchaseOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrderDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!user) {
      navigate('/auth/login')
      return
    }

    if (!id) {
      navigate('/enterprise/post-quote-activity/purchase-orders')
      return
    }

    loadPurchaseOrder()
  }, [user, navigate, id])

  const loadPurchaseOrder = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await apiService.get(`/api/post-quote-activity/purchase-orders/${id}`) as any
      
      if (response.success) {
        setPurchaseOrder(response.data)
      } else {
        setError('Failed to load purchase order details')
      }
    } catch (err: any) {
      console.error('Error loading purchase order:', err)
      if (err.response?.status === 404) {
        setError('Purchase order not found')
      } else if (err.response?.status === 403) {
        setError('Access denied')
      } else {
        setError('Failed to load purchase order details')
      }
      toast.error(err.response?.data?.message || 'Failed to load purchase order details')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800'
      case 'ACCEPTED':
        return 'bg-green-100 text-green-800'
      case 'IN_REVIEW':
        return 'bg-blue-100 text-blue-800'
      case 'REJECTED':
        return 'bg-red-100 text-red-800'
      case 'CANCELLED':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <Clock className="w-4 h-4" />
      case 'ACCEPTED':
        return <CheckCircle className="w-4 h-4" />
      case 'IN_REVIEW':
        return <AlertCircle className="w-4 h-4" />
      case 'REJECTED':
      case 'CANCELLED':
        return <XCircle className="w-4 h-4" />
      default:
        return <Clock className="w-4 h-4" />
    }
  }

  const formatCurrency = (amount: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(amount / 100) // Convert from cents
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star 
        key={i} 
        className={`w-4 h-4 ${i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
      />
    ))
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !purchaseOrder) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <XCircle className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Error Loading Purchase Order</h3>
          <p className="mt-1 text-sm text-gray-500">{error}</p>
          <div className="mt-6">
            <button
              onClick={() => navigate('/enterprise/post-quote-activity/purchase-orders')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Purchase Orders
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/enterprise/post-quote-activity/purchase-orders')}
              className="p-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Purchase Order Details</h1>
              <p className="text-sm text-gray-600">View and manage purchase order information</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className={`px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1 ${getStatusColor(purchaseOrder.status)}`}>
              {getStatusIcon(purchaseOrder.status)}
              <span>{purchaseOrder.status}</span>
            </div>
            <button
              onClick={() => navigate(`/enterprise/post-quote-activity/purchase-orders/${purchaseOrder.id}/edit`)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Purchase Order Information */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center space-x-2 mb-4">
              <ShoppingCart className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Purchase Order Information</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PO Number</label>
                <p className="text-sm text-gray-900 font-mono bg-gray-50 p-2 rounded">{purchaseOrder.poNumber}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount</label>
                <p className="text-sm text-gray-900 font-semibold">{formatCurrency(purchaseOrder.totalAmount, purchaseOrder.currency)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Created</label>
                <p className="text-sm text-gray-900">{formatDate(purchaseOrder.createdAt)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
                <p className="text-sm text-gray-900">{formatDate(purchaseOrder.updatedAt)}</p>
              </div>
            </div>

            {purchaseOrder.notes && (
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded">{purchaseOrder.notes}</p>
              </div>
            )}
          </div>

          {/* RFQ Information */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center space-x-2 mb-4">
              <FileText className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">Related RFQ</h2>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">RFQ Title</label>
                <p className="text-sm text-gray-900">{purchaseOrder.rfq.title}</p>
              </div>
              {purchaseOrder.rfq.description && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <p className="text-sm text-gray-900">{purchaseOrder.rfq.description}</p>
                </div>
              )}
            </div>
          </div>

          {/* Fulfillments */}
          {purchaseOrder.fulfillments.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Truck className="w-5 h-5 text-orange-600" />
                <h2 className="text-lg font-semibold text-gray-900">Fulfillment Tracking</h2>
              </div>
              
              <div className="space-y-4">
                {purchaseOrder.fulfillments.map((fulfillment) => (
                  <div key={fulfillment.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(fulfillment.status)}`}>
                        {fulfillment.status}
                      </div>
                      <span className="text-xs text-gray-500">{formatDate(fulfillment.createdAt)}</span>
                    </div>
                    
                    {fulfillment.trackingNumber && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">Tracking:</span>
                          <span className="ml-2 font-mono">{fulfillment.trackingNumber}</span>
                        </div>
                        {fulfillment.carrierName && (
                          <div>
                            <span className="font-medium text-gray-700">Carrier:</span>
                            <span className="ml-2">{fulfillment.carrierName}</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {(fulfillment.estimatedDelivery || fulfillment.actualDelivery) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mt-2">
                        {fulfillment.estimatedDelivery && (
                          <div>
                            <span className="font-medium text-gray-700">Est. Delivery:</span>
                            <span className="ml-2">{formatDate(fulfillment.estimatedDelivery)}</span>
                          </div>
                        )}
                        {fulfillment.actualDelivery && (
                          <div>
                            <span className="font-medium text-gray-700">Delivered:</span>
                            <span className="ml-2">{formatDate(fulfillment.actualDelivery)}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Invoices */}
          {purchaseOrder.invoices.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center space-x-2 mb-4">
                <DollarSign className="w-5 h-5 text-green-600" />
                <h2 className="text-lg font-semibold text-gray-900">Invoices</h2>
              </div>
              
              <div className="space-y-4">
                {purchaseOrder.invoices.map((invoice) => (
                  <div key={invoice.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900">{invoice.invoiceNumber}</h4>
                        <p className="text-sm text-gray-600">{formatCurrency(invoice.amount)}</p>
                      </div>
                      <div className="text-right">
                        <div className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(invoice.status)}`}>
                          {invoice.status}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Due: {formatDate(invoice.dueDate)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Feedback */}
          {purchaseOrder.feedback.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center space-x-2 mb-4">
                <MessageSquare className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">Vendor Feedback</h2>
              </div>
              
              <div className="space-y-4">
                {purchaseOrder.feedback.map((feedback) => (
                  <div key={feedback.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        {renderStars(feedback.rating)}
                        <span className="text-sm font-medium text-gray-900">{feedback.rating}/5</span>
                      </div>
                      <span className="text-xs text-gray-500">{formatDate(feedback.createdAt)}</span>
                    </div>
                    
                    {feedback.review && (
                      <p className="text-sm text-gray-700">{feedback.review}</p>
                    )}
                    
                    <div className="mt-3 text-xs text-gray-500">
                      By: {feedback.buyer.companyName || `${feedback.buyer.firstName} ${feedback.buyer.lastName}`.trim()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Party Information */}
        <div className="space-y-6">
          {/* Buyer Information */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center space-x-2 mb-4">
              <User className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Buyer</h2>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                <p className="text-sm text-gray-900">{purchaseOrder.buyer.companyName || 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact</label>
                <p className="text-sm text-gray-900">
                  {purchaseOrder.buyer.firstName && purchaseOrder.buyer.lastName 
                    ? `${purchaseOrder.buyer.firstName} ${purchaseOrder.buyer.lastName}`
                    : 'Not specified'
                  }
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <p className="text-sm text-gray-900">{purchaseOrder.buyer.email}</p>
              </div>
            </div>
          </div>

          {/* Vendor Information */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Building2 className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">Vendor</h2>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                <p className="text-sm text-gray-900">{purchaseOrder.vendor.companyName || 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact</label>
                <p className="text-sm text-gray-900">
                  {purchaseOrder.vendor.firstName && purchaseOrder.vendor.lastName 
                    ? `${purchaseOrder.vendor.firstName} ${purchaseOrder.vendor.lastName}`
                    : 'Not specified'
                  }
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <p className="text-sm text-gray-900">{purchaseOrder.vendor.email}</p>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/enterprise/post-quote-activity/fulfillments')}
                className="w-full text-left px-4 py-3 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <Truck className="w-4 h-4 text-orange-600" />
                  <div>
                    <p className="font-medium text-gray-900">View Fulfillments</p>
                    <p className="text-xs text-gray-600">Track delivery status</p>
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => navigate('/enterprise/post-quote-activity/invoices')}
                className="w-full text-left px-4 py-3 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <DollarSign className="w-4 h-4 text-green-600" />
                  <div>
                    <p className="font-medium text-gray-900">View Invoices</p>
                    <p className="text-xs text-gray-600">Manage payments</p>
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => navigate('/enterprise/post-quote-activity/feedback')}
                className="w-full text-left px-4 py-3 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <MessageSquare className="w-4 h-4 text-purple-600" />
                  <div>
                    <p className="font-medium text-gray-900">Provide Feedback</p>
                    <p className="text-xs text-gray-600">Rate vendor performance</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PurchaseOrderDetailPage